import { Link } from 'react-router-dom';
import type { RepoScore, TeamRepoScore } from '@sdd-maturity/shared-types';
import { FrameworkBadge } from './FrameworkBadge';
import { relativeTime, isStale, repoStatus } from '../lib/utils';
import { levelLabel, levelBadgeClass } from '../lib/score';
import { buildRepoPath } from '../lib/hierarchy-route';

export interface RepoCardProps {
  repo: RepoScore | TeamRepoScore;
  /** FR-5: owning team, threaded into the canonical leaf URL. */
  teamNumber?: string;
  showComponent?: boolean;
}

export function RepoCard({ repo, teamNumber, showComponent }: RepoCardProps) {
  const teamRepo = 'componentName' in repo ? (repo as TeamRepoScore) : undefined;
  // Single shared classifier (P3/FR-1). `notScored` (maintenance/infra, score -1)
  // renders through the same dimmed "Eval Failed" branch it always has here — the
  // card has no separate not-scored treatment. Only `active` gets the normal body.
  const { status } = repoStatus(repo as TeamRepoScore);
  const isInaccessible = status === 'inaccessible';
  const isEvalFailed = status === 'evalFailed' || status === 'notScored';
  const isDimmed = status !== 'active';
  const repoAny = repo as unknown as Record<string, unknown>;
  const daysInactive = repoAny['daysInactive'] as number | undefined;
  const lastActivity = (repoAny['lastActivityDate'] as string | undefined)
    ?? teamRepo?.lastActivityDate;

  if (isDimmed) {
    return (
      <div
        className="card card-dimmed flex items-center justify-between"
        data-testid={`repo-card-${repo.repoName}`}
      >
        <div className="flex items-center gap-sm">
          <span className="font-semibold">{repo.repoName}</span>
          <span className={`badge ${isEvalFailed ? 'badge-l1' : 'badge-warning'}`}>
            {isEvalFailed ? 'Eval Failed' : isInaccessible ? 'Not Accessible' : 'Skipped'}
          </span>
          {showComponent && teamRepo?.componentName && (
            <span className="badge badge-info">{teamRepo.componentName}</span>
          )}
        </div>
        <div className="flex items-center gap-md">
          <span className="text-sm text-muted">
            {isEvalFailed
              ? 'Could not evaluate'
              : isInaccessible
              ? 'Private or token lacks access'
              : `Inactive ${daysInactive ? `${daysInactive} days` : lastActivity ? relativeTime(lastActivity) : '30+ days'}`}
          </span>
        </div>
      </div>
    );
  }

  // Canonical leaf URL only (owning team in the path). Without a team there is no
  // valid path, so the card renders as a non-interactive div.
  const body = (
    <>
      <div className="flex items-center gap-sm">
        <span className="font-semibold">{repo.repoName}</span>
        <span className={levelBadgeClass(repo.overallLevel)}>{levelLabel(repo.overallLevel)}</span>
        <FrameworkBadge framework={repo.framework} />
        {showComponent && teamRepo?.componentName && (
          <span className="badge badge-info">{teamRepo.componentName}</span>
        )}
        {teamRepo?.lastActivityDate && isStale(teamRepo.lastActivityDate) && (
          <span className="badge badge-warning" data-testid="stale-badge">Stale</span>
        )}
      </div>
      <div className="flex items-center gap-md">
        {teamRepo?.lastActivityDate && (
          <span className="text-xs text-muted" data-testid="activity-date">
            {relativeTime(teamRepo.lastActivityDate)}
          </span>
        )}
        {teamRepo?.commitCount60d !== undefined && teamRepo.commitCount60d > 0 && (
          <span className="badge badge-info" data-testid="commit-count">
            {teamRepo.commitCount60d} commits
          </span>
        )}
        <span className="text-lg font-bold">{repo.overallScore.toFixed(1)}</span>
        <span className="text-xs text-muted">
          {repo.findingsCount.open} open / {repo.findingsCount.resolved} resolved
        </span>
      </div>
    </>
  );

  return teamNumber ? (
    <Link
      to={buildRepoPath(teamNumber, repo.orgName, repo.repoName)}
      className="card-interactive card-link flex items-center justify-between"
      data-testid={`repo-card-${repo.repoName}`}
    >
      {body}
    </Link>
  ) : (
    <div className="card flex items-center justify-between" data-testid={`repo-card-${repo.repoName}`}>
      {body}
    </div>
  );
}
